/**
 * Crear un producto desde el alta guiada — lo que pasa al apretar «Crear».
 *
 * Vivía adentro de Catálogo. Sale acá porque Inventario también da de alta:
 * el que ingresa mercadería y no la encuentra la crea ahí mismo, sin irse a
 * otra pantalla y volver. Una sola copia de «crear y, si hay unidades,
 * ingresarlas» hace lo mismo desde las dos puertas.
 */
import { mostrarError, mostrarExito } from "@/lib/errores";
import { useCrearProducto, useIngresoStock } from "@/lib/queries";
import { type Producto, type Rubro } from "@/lib/tipos";

import { aProductoNuevo, type FormAlta, type TipoAlta } from "./logica";

export interface CreacionGuiada {
  /** El producto creado, o `null` si algo falló (el error ya se mostró). */
  crear: (tipo: TipoAlta, form: FormAlta) => Promise<Producto | null>;
  guardando: boolean;
}

/**
 * `cotizacion` hace falta para despejar el margen cuando el costo y el precio
 * van en monedas distintas (ver `aProductoNuevo`). Sin ella el precio queda
 * fijo, como antes.
 */
export function useCrearGuiado(rubros: ReadonlyArray<Rubro>, cotizacion = 0): CreacionGuiada {
  const crearProducto = useCrearProducto();
  const ingresar = useIngresoStock();

  async function crear(tipo: TipoAlta, form: FormAlta): Promise<Producto | null> {
    try {
      const nuevo = await crearProducto.mutateAsync(
        aProductoNuevo(tipo, form, [...rubros], cotizacion),
      );
      if (form.unidades && form.unidades > 0) {
        await ingresar.mutateAsync({
          producto_id: nuevo.id,
          cantidad: form.unidades,
          tipo: "COMPRA",
          actualiza_costo: false,
          referencia: "ALTA",
        });
      }
      mostrarExito(
        `${nuevo.nombre} quedó cargado`,
        `Código ${nuevo.codigo_interno}${form.unidades && form.unidades > 0 ? ` · ${form.unidades} en stock` : ""}`,
      );
      return nuevo;
    } catch (error) {
      mostrarError(error);
      return null;
    }
  }

  return { crear, guardando: crearProducto.isPending || ingresar.isPending };
}
